import type { ReactNode } from 'react'
import { Group, Stack, Text, Tooltip } from '@mantine/core'
import { OpenInMc1Link } from './OpenInMc1Link'
import { Button } from '../Common/Button'

export interface ClaimHeaderProps {
  title: string
  claimNumber: string
  insuredName: string
  insurerName: string
  dateOfLoss: string
  // Current status shown under the DOL. The editable copy stays in the Summary
  // tab's key data (MSD-538).
  presentPosition?: string
  // MC1 case id — makes the claim number a "View claim in MC1" link.
  caseId: number
  // Disconnects the claim — the Connected button is a toggle, so clicking it
  // returns to the search page.
  onDisconnect: () => void
  // Rendered on the left of the Connected row (Attach to MC1).
  leftAction?: ReactNode
}

export function ClaimHeader({
  title,
  claimNumber,
  insuredName,
  insurerName,
  dateOfLoss,
  presentPosition,
  caseId,
  onDisconnect,
  leftAction,
}: Readonly<ClaimHeaderProps>) {
  return (
    <Stack gap={4}>
      <Group justify="space-between" wrap="nowrap">
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Tooltip label={title} withArrow openDelay={400} multiline maw={300}>
            <Text fw={600} fz="sm" truncate>
              {title}
            </Text>
          </Tooltip>
        </Stack>
        <OpenInMc1Link claimNumber={claimNumber} caseId={caseId} />
      </Group>
      <Group justify="space-between" wrap="nowrap">
        <Text fz="xs" truncate>
          Insurer: {insurerName}
        </Text>
        <Text fz="xs" style={{ whiteSpace: 'nowrap' }}>
          DOL: {dateOfLoss}
        </Text>
      </Group>
      <Group justify="space-between" align="center" wrap="nowrap">
        <Text fz="xs" truncate style={{ minWidth: 0 }}>
          Insured: {insuredName}
        </Text>
        {presentPosition && (
          <Tooltip label={presentPosition} withArrow openDelay={400} multiline maw={300}>
            <Text fz="xs" truncate style={{ minWidth: 0 }}>
              {presentPosition}
            </Text>
          </Tooltip>
        )}
      </Group>
      {/* Top-aligned so Connected stays level with the left action when text appears under it. */}
      <Group justify={leftAction ? 'space-between' : 'flex-end'} align="flex-start" wrap="nowrap">
        {leftAction}
        <Button label="Connected" variant="filled" color="success.6" size="xs" onClick={onDisconnect} />
      </Group>
    </Stack>
  )
}
